/**
 * Instructor Service
 *
 * API client for instructor directory and search endpoints
 */

import { apiClient } from './apiClient';

export interface LocationData {
  country: string | null;
  state: string | null;
  city: string | null;
}

export interface MaterialsData {
  has_crystals_bed: boolean;
  has_arrow: boolean;
  has_steel_bars: boolean;      // Barras de acero
  has_concrete_blocks: boolean; // Bloques hormigón
  has_other: boolean;           // Otro
  other_description: string;    // Text field for "Otro" description
}

export interface ProvidersData {
  provides_wood: boolean;
  provides_tepe: boolean;
  provides_iron_bar: boolean;
  provides_arrows: boolean;
  provides_wood_board: boolean;
  provides_concrete_block: boolean;
}

export interface InstructorCard {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  photo: string | null;
  location: LocationData;
  specialties: string[] | null;  // Now an array of specialties
  specialtiesPreview: string | null;
  verified: boolean;
  nivel_de_experiencia_en_fuego: string | null;
  certification?: {
    school: string | null;
    year: string | null;
    master: string | null;
  };
  materials?: MaterialsData;
  providers?: ProvidersData;
  is_professional?: boolean;
}

export interface InstructorProfile {
  id: string;
  basic_info: {
    name: string;
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    photo: string | null;
    verified: boolean;
  };
  location: {
    country: string | null;
    state: string | null;
    city: string | null;
    address: string | null;
  };
  certification: {
    school: string | null;
    year: string | null;
    master: string | null;
  };
  professional: {
    is_professional: boolean;
    specialties: string[] | null;  // Now an array of specialties
    nivel_de_experiencia_en_fuego: string | null;
    story: string | null;
  };
  materials: MaterialsData;
  providers: ProvidersData;
  metadata: {
    createdAt: string | null;
    updatedAt: string | null;
  };
}

export interface SearchFilters {
  country?: string;
  state?: string;
  city?: string;
  materials?: string[]; // Array of material IDs (has_crystals_bed, has_arrow, complemento_material_dai)
  providers?: string[]; // Array of provider IDs (provides_wood, provides_tepe, etc.)
  nivel_de_experiencia_en_fuego?: string; // Cerilla, Brasero, Horno, Vulcano
  verified?: boolean;
  query?: string; // General text search
}

export interface PaginationData {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  hasMore: boolean;
}

export interface SearchResults {
  instructors: InstructorCard[];
  pagination: PaginationData;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface AvailableFilters {
  nivel_de_experiencia_en_fuego: FilterOption[];
  materials: FilterOption[];
  providers: FilterOption[];
}

/**
 * Search instructors with filters and pagination
 *
 * @param filters Search filters
 * @param page Page number (default: 1)
 * @param perPage Results per page (default: 20)
 * @returns Search results with instructors and pagination metadata
 */
export async function searchInstructors(
  filters: SearchFilters = {},
  page: number = 1,
  perPage: number = 20
): Promise<SearchResults> {
  // Build query parameters
  const params = new URLSearchParams();

  // Location filters
  if (filters.country) params.append('country', filters.country);
  if (filters.state) params.append('state', filters.state);
  if (filters.city) params.append('city', filters.city);

  // Materials filter (comma-separated)
  if (filters.materials && filters.materials.length > 0) {
    params.append('materials', filters.materials.join(','));
  }

  // Providers filter (comma-separated)
  if (filters.providers && filters.providers.length > 0) {
    params.append('providers', filters.providers.join(','));
  }

  // Nivel de experiencia en fuego filter
  if (filters.nivel_de_experiencia_en_fuego) {
    params.append('nivel_de_experiencia_en_fuego', filters.nivel_de_experiencia_en_fuego);
  }

  // Verified filter
  if (filters.verified !== undefined) {
    params.append('verified', filters.verified ? '1' : '0');
  }

  // General text search
  if (filters.query) {
    params.append('query', filters.query);
  }

  // Pagination
  params.append('page', page.toString());
  params.append('per_page', perPage.toString());

  const response = await apiClient.request<SearchResults>(
    `/instructors?${params.toString()}`,
    {
      method: 'GET'
    }
  );

  return response.data as SearchResults;
}

/**
 * Get instructor by ID
 *
 * @param id Instructor HubSpot contact ID
 * @returns Complete instructor profile
 */
export async function getInstructor(id: string): Promise<InstructorProfile> {
  const response = await apiClient.request<InstructorProfile>(
    `/instructors/${id}`,
    {
      method: 'GET'
    }
  );

  return response.data as InstructorProfile;
}

/**
 * Get available filter options
 *
 * @returns Available filter options for search
 */
export async function getAvailableFilters(): Promise<AvailableFilters> {
  const response = await apiClient.request<AvailableFilters>(
    '/search/filters',
    {
      method: 'GET'
    }
  );

  return response.data as AvailableFilters;
}
